import { DynamicIcon } from '@/components/ui/DynamicIcon'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  type AnimateLayoutChanges,
  arrayMove,
  defaultAnimateLayoutChanges,
} from '@dnd-kit/sortable'
import { type ClassValue, clsx } from 'clsx'
import type { ConfigType } from 'dayjs'
import type { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { z } from 'zod/v4'
import dayjs from './dayjs'
import { TransactionLineType } from './enums/TransactionLineType'

const numberFormatter = new Intl.NumberFormat('en-US')

/** Amounts arrive as numbers or as API/form strings, and may be missing. */
export type NumericInput = number | string | null | undefined

// Reads `value?.[key]` from any value without assuming its shape.
export function readProperty(value: unknown, key: string): unknown {
  if ((typeof value === 'object' && value !== null) || typeof value === 'function') {
    return Reflect.get(value, key)
  }

  return undefined
}

// Narrows an unknown value to an object whose fields can be read. Arrays count as objects too.
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

export function sanitizeId(value: unknown): string {
  return String(value).replace(/[^a-zA-Z0-9_-]/g, '_')
}

export function normalizeEmailInput(value: string | null | undefined): string {
  return String(value ?? '')
    .trimStart()
    .toLowerCase()
}

export function toYmdFromDate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function toCalendarDate(date: ConfigType): Date {
  // ymd is YYYY-MM-DD (or anything dayjs can parse in the current tz).
  // Noon avoids boundary issues when JS Date is interpreted in browser tz.
  return dayjs(date).startOf('day').add(12, 'hour').toDate()
}

export function normalizeCalendarDateInput<T>(value: T): string | T {
  const dateText = String(value ?? '')
  const calendarDateMatch = dateText.match(/^\d{4}-\d{2}-\d{2}/)

  return calendarDateMatch?.[0] ?? value
}

export function formatCalendarDate(
  value: ConfigType,
  format = 'MMM D, YYYY',
  fallback = ''
): string {
  return value ? dayjs(normalizeCalendarDateInput(value)).format(format) : fallback
}

export const normalizeCurrencyValue = (amount: NumericInput): number =>
  Number(parseCurrency(amount ?? 0).toFixed(2))

export function generateIdempotencyKey(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }

  return `idmp_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`
}

/**
 * Combines a date string (YYYY-MM-DD) and time string (HH:mm, defaults to '00:00') into a
 * YYYY-MM-DD HH:mm:ss datetime string, or null if date is not provided.
 * formatDateTime('2025-01-15', '14:30') => '2025-01-15 14:30:00'
 * formatDateTime('2025-01-15') => '2025-01-15 00:00:00'
 */
export function formatDateTime(date: string | null | undefined, time = '00:00'): string | null {
  if (!date) return null
  return dayjs(`${date} ${time}`).format('YYYY-MM-DD HH:mm:ss')
}

/** The parts of a TanStack Form array field that drag-and-drop reorders. */
export type SortableArrayField<T extends { id?: unknown }> = {
  state: { value: T[] }
  setValue: (updater: (oldValue: T[]) => T[]) => void
}

// This is a generic method for handling drag and drop with tanstack/form array fields and dnd-kit
export const handleDragEnd =
  <T extends { id?: unknown }>(field: SortableArrayField<T>) =>
  ({ active, over }: Pick<DragEndEvent, 'active' | 'over'>): void => {
    // Dropping outside every droppable leaves `over` empty, so there is no new position.
    if (!over) return

    if (active?.id !== over.id) {
      const oldIndex = field.state.value.findIndex((x) => x.id === active.id)
      const newIndex = field.state.value.findIndex((x) => x.id === over.id)
      field.setValue((oldValue) => arrayMove(oldValue, oldIndex, newIndex))
    }
  }

export const wait = (ms: number): Promise<void> =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

export const slugify = (str: string): string =>
  str
    .toString() // Ensure input is string
    .toLowerCase() // Convert to lowercase
    .replace(/\s+/g, '-') // Replace whitespace sequences with single dash
    .replace(/[^a-z0-9-]/g, '') // Remove non-alphanumeric except dashes
    .replace(/-{2,}/g, '-') // Replace multiple consecutive dashes with single dash
    .replace(/^-+/, '') // Remove leading dashes
    .replace(/-+$/, '') // Remove trailing dashes

/**
 * Reads an API money value (decimal string or JSON number) or a typed amount as a number.
 * Strips currency symbols and thousands separators; missing or unreadable values read as 0.
 * parseCurrency("$100.50") => 100.5
 */
export const parseCurrency = (amount: NumericInput): number => {
  // Numbers skip the string cleanup, which would mangle exponent forms such as 1e-7.
  if (typeof amount === 'number') {
    return Number.isFinite(amount) ? amount : 0
  }

  // regex to remove any non number or period chars
  const cleaned = amount?.toString()?.replace(/[^0-9.-]/g, '')
  const parsed = Number.parseFloat(cleaned || '0')

  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * Formats a number into a currency string with 2 decimal places.
 * Strings go through parseCurrency first; any other non-number formats as zero.
 * Currency defaults to 'USD'.
 * formatCurrency(100.5) => "$100.50"
 * formatCurrency(100.5, 'EUR') => "€100.50"
 */
export const formatCurrency = (amount: unknown, currency = 'USD'): string => {
  const normalizedAmount = typeof amount === 'string' ? parseCurrency(amount) : amount

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(
    typeof normalizedAmount === 'number' && Number.isFinite(normalizedAmount) ? normalizedAmount : 0
  )
}

/**
 * Parses a currency string and then formats it.
 * Convenience function that combines parseCurrency and formatCurrency.
 * parseAndFormatCurrency("$100.50") => "$100.50"
 */
export const parseAndFormatCurrency = (amount: NumericInput): string => {
  return formatCurrency(parseCurrency(amount))
}

/**
 * Formats a number with US thousands separators.
 * formatNumber(11485) => "11,485"
 */
export const formatNumber = (value: unknown): string => {
  const normalizedValue = typeof value === 'string' ? Number(value) : value

  return numberFormatter.format(
    typeof normalizedValue === 'number' && Number.isFinite(normalizedValue) ? normalizedValue : 0
  )
}

/**
 * Formats a currency input value for editable form controls.
 * Returns a plain decimal string without currency symbols or separators.
 */
export const formatCurrencyInputValue = (
  amount: NumericInput,
  { formatEmptyAsZero = true }: { formatEmptyAsZero?: boolean } = {}
): string => {
  if (!formatEmptyAsZero && String(amount ?? '').trim() === '') {
    return ''
  }

  return parseAndFormatCurrency(amount)?.replace(/[$,-]/g, '')
}

/**
 * Formats a number as a percentage.
 * decimalPlaces is the number of decimal places to include (default is 2).
 * isOver100Percent says whether the number can be over 100% (default is false).
 *
 * formatPercent(0.5) => "50.00%"
 * formatPercent(0.555, 1) => "55.5%"
 * formatPercent(55, 1) => "55.0%" (automatically divides by 100)
 * formatPercent(55, 1, true) => "5,555.0%" (if isOver100Percent param is true, then no dividing by 100)
 */
export const formatPercent = (
  amount: number | null | undefined,
  decimalPlaces = 2,
  isOver100Percent = false
): string => {
  if (typeof amount === 'undefined') return ''

  if (amount !== null && amount > 1 && !isOver100Percent) {
    amount /= 100
  }

  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(amount || 0)
}

export function parsePercent(amount: NumericInput): string {
  if (typeof amount === 'undefined') {
    return ''
  }

  const num = parseFloat(String(amount))
  if (typeof num === 'number' && Number.isFinite(num)) {
    const pct = num <= 1 ? num * 100 : num
    return pct.toFixed(2)
  }

  return ''
}

export const safeJsonParse = (json: unknown): unknown => {
  try {
    return JSON.parse(String(json))
  } catch {
    return null
  }
}

/**
 * Merge all the properties from mergeThisObj into intoThisObj as long as
 * the properties in mergeThisObj are not undefined. This is useful for
 * building queryKeys with optional parameters. For example
 * user: (userId, filters, optionalArg) => [
 *   conditionalObjectMerge(
 *     { userId, filters, optionalArg },
 *     { ...key.all[0], entity: "user", scope: "show" }
 *   )
 * ]
 */
export function conditionalObjectMerge<M extends object>(mergeThisObj?: M): Partial<M>
export function conditionalObjectMerge<M extends object, I extends object>(
  mergeThisObj: M | undefined,
  intoThisObj: I
): I & Partial<M>
export function conditionalObjectMerge(mergeThisObj: object = {}, intoThisObj: object = {}) {
  const entries: [string, unknown][] = Object.entries(mergeThisObj)
  const definedProps = Object.fromEntries(
    entries.filter(([, value]) => typeof value !== 'undefined')
  )
  return Object.assign(intoThisObj, definedProps)
}

/**
 * I made this to conditionally create an object to use for making queryKeys.
 *
 * condition - If this is true, then the newEntries will be added to the existingEntries.
 * newEntries - An object containing key/value pairs you only want in the final object if condition is true
 * existingEntries - An optional object of entries you know you want in the final object
 *
 * Examples:
 *
 * // Without existingEntries...
 * conditionalObject(true, { filter: 'active' });
 * // => { filter: 'active' }
 *
 * conditionalObject(false, { filter: 'active' });
 * // => undefined
 *
 * // With existingEntries...
 * const existingEntries = { org: 'acme' };
 *
 * conditionalObject(true, { filter: 'active' }, existingEntries);
 * // => { org: 'acme', filter: 'active' }
 *
 * conditionalObject(false, { filter: 'active' }, existingEntries);
 * // => { org: 'acme' } (returns the existingEntries object)
 */
export const conditionalObject = <N extends object, E extends object>(
  condition: boolean,
  newEntries: N,
  existingEntries?: E
): E | N | (E & N) | undefined => {
  if (!condition) return existingEntries

  if (existingEntries) return { ...existingEntries, ...newEntries }

  return Object.keys(newEntries).length ? newEntries : undefined
}

export function animateLayoutChanges(args: Parameters<AnimateLayoutChanges>[0]): boolean {
  const { isSorting, wasDragging } = args
  if (isSorting || wasDragging) {
    return defaultAnimateLayoutChanges(args)
  }
  return true
}

export function toTitleCase(str: unknown): string {
  // prettier-ignore
  const minorWords = ['a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'on', 'at', 'to', 'from', 'by', 'of', 'in', 'with']

  return String(str)
    .toLowerCase()
    .split(' ')
    .map((word, index, words) => {
      // Always capitalize first and last words
      if (index === 0 || index === words.length - 1) {
        return word.charAt(0).toUpperCase() + word.slice(1)
      }
      // Lowercase minor words, capitalize others
      return minorWords.includes(word) ? word : word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}

/** A value with a truthy `code`, `path` and `message`, like a Zod issue. */
export type ZodIssueLike = { code: unknown; path: unknown; message: unknown }

export const isZodError = (error: unknown): error is ZodIssueLike =>
  Boolean(
    readProperty(error, 'code') && readProperty(error, 'path') && readProperty(error, 'message')
  )

type RequiredIdSchema = z.ZodCoercedNumber
type OptionalIdSchema = z.ZodOptional<z.ZodUnion<readonly [z.ZodLiteral<''>, z.ZodCoercedNumber]>>

/** A positive integer id; optional ids also accept `''` (an empty field) and a missing value. */
export function idSchema(model: string, isOptional?: false): RequiredIdSchema
export function idSchema(model: string, isOptional: true): OptionalIdSchema
export function idSchema(model: string, isOptional = false): RequiredIdSchema | OptionalIdSchema {
  const error = `Invalid ${model} ID`
  if (isOptional) {
    return z.union([z.literal(''), z.coerce.number(error).int(error).positive(error)]).optional()
  }
  return z.coerce.number(error).int(error).positive(error)
}

// Undefined when an API error response carries no message, so callers can supply their own.
export function getErrorMessage(error: unknown): string | undefined {
  if (typeof error === 'string') {
    return error
  }

  // Zod issue messages are written for people, so show them as-is.
  if (isZodError(error) && typeof error.message === 'string') {
    return error.message
  }

  // Recursively map through arrays
  if (Array.isArray(error)) {
    const errors: unknown[] = error
    return errors.map(getErrorMessage).join(', ')
  }

  // Handle route search validation errors
  const causeIssues = readProperty(readProperty(error, 'cause'), 'issues')
  if (causeIssues) {
    return getErrorMessage(causeIssues)
  }

  // Handle axios responses
  const response = readProperty(error, 'response')
  if (response) {
    const responseData = readProperty(response, 'data')
    const responseMessage = readProperty(responseData, 'message')
    const validationErrors: unknown[] = Object.values(readProperty(responseData, 'errors') ?? {})
    const validationMessage = validationErrors
      .flat()
      .find(
        (message): message is string => typeof message === 'string' && message.trim().length > 0
      )

    if (
      typeof responseMessage === 'string' &&
      responseMessage.trim().length > 0 &&
      responseMessage !== 'Validation failed'
    ) {
      return responseMessage
    }

    return validationMessage || (typeof responseMessage === 'string' ? responseMessage : undefined)
  }

  // Error instances from another browser frame fail the local instanceof check.
  const message = readProperty(error, 'message')
  if (typeof message === 'string' && message.trim().length > 0) {
    return message
  }

  if (error instanceof Error) {
    return error.toString()
  }

  // For objects, loop over the entries
  if (typeof error === 'object' && error !== null) {
    const values: unknown[] = Object.values(error)
    return values.map((value) => getErrorMessage(value)).join(', ')
  }

  // Final fallback
  return JSON.stringify(error, null, 2)
}

export const unslugify = (stringSlug: string | null | undefined): string => {
  let str = stringSlug
  if (!str) {
    return ''
  }
  str = str.replace(/_/g, ' ')
  str = str.replace(/-/g, ' ')
  str = toTitleCase(str)

  return str
}

/** One entry of a generated `@/lib/enums` object. */
export type EnumEntry = {
  Name: string
  Value: string
  Label?: string
  Icon?: string
}

export type EnumSelectOption<TId extends string = string> = {
  id: TId
  name: ReactNode
  icon: string | undefined
}

export type EnumToSelectOptionsConfig<E extends EnumEntry> = {
  includeEmpty?: boolean
  emptyLabel?: string
  emptyValue?: string
  includeIcons?: boolean
  filter?: ((enumValue: E, index: number, enumValues: E[]) => boolean) | null
  labelOverrides?: Partial<Record<string, string>>
}

// Without an empty option or icons, ids keep the enum values and labels stay plain text.
export function enumToSelectOptions<E extends EnumEntry>(
  enumObj: Record<string, E>,
  options?: EnumToSelectOptionsConfig<E> & { includeEmpty?: false; includeIcons?: false }
): (EnumSelectOption<E['Value']> & { name: string })[]
// Without an empty option, every id remains one of the enum's actual values.
export function enumToSelectOptions<E extends EnumEntry>(
  enumObj: Record<string, E>,
  options?: EnumToSelectOptionsConfig<E> & { includeEmpty?: false }
): EnumSelectOption<E['Value']>[]
export function enumToSelectOptions<E extends EnumEntry>(
  enumObj: Record<string, E>,
  options: EnumToSelectOptionsConfig<E>
): EnumSelectOption[]
export function enumToSelectOptions<E extends EnumEntry>(
  enumObj: Record<string, E>,
  options: EnumToSelectOptionsConfig<E> = {}
): EnumSelectOption[] {
  const {
    includeEmpty = false,
    emptyLabel = 'Select...',
    emptyValue = '',
    includeIcons = false,
    filter = null,
    labelOverrides = {},
  } = options

  const optionsArray = Object.values(enumObj)
    .filter(filter || (() => true))
    .map((enumValue): EnumSelectOption => {
      let name: ReactNode = labelOverrides[enumValue.Value] ?? enumValue.Label ?? enumValue.Name

      if (includeIcons && enumValue.Icon) {
        name = (
          <span className="flex items-center gap-2">
            <DynamicIcon iconName={enumValue.Icon} className="size-4" />
            {name}
          </span>
        )
      }

      return {
        id: enumValue.Value,
        name,
        icon: includeIcons ? enumValue.Icon : undefined,
      }
    })

  if (includeEmpty) {
    optionsArray.unshift({
      id: emptyValue,
      name: emptyLabel,
      icon: undefined,
    })
  }

  return optionsArray
}

export function getEnumDisplayName(enumObj: Record<string, EnumEntry>, value: unknown): string {
  const enumEntry = Object.values(enumObj).find((entry) => entry.Value === value)
  return enumEntry?.Label || enumEntry?.Name || toTitleCase(value)
}

export type QueryFilterParams = Record<string, unknown> & {
  include?: string[]
  exclude?: string[]
}

export class QueryFilters {
  params: QueryFilterParams

  constructor(params: QueryFilterParams = { include: [], exclude: [] }) {
    this.params = params
  }

  get(): QueryFilterParams {
    return { ...this.params }
  }

  defaultsWith(params: Pick<QueryFilterParams, 'include' | 'exclude'>): this {
    if (params.include) {
      this.params.include = [...(this.params.include ?? []), ...params.include]
    }

    if (params.exclude) {
      this.params.exclude = [...(this.params.exclude ?? []), ...params.exclude]
    }
    return this
  }

  include(includeModels: string[]): this {
    this.params = { ...this.params, include: includeModels }
    return this
  }

  exclude(excludeFields: string[]): this {
    this.params = { ...this.params, exclude: excludeFields }
    return this
  }

  applyFilter(filters: Record<string, unknown>): this {
    this.params = { ...this.params, ...filters }
    return this
  }
}

/** The document line fields the returned-status helper reads. */
export type InvoiceLineItemLike = {
  id: number | string
  type?: string | null
  quantity?: NumericInput
}

/** The order fields the returned-status helper reads: its document and what refunds credited of each line. */
export type OrderWithInvoiceLike<L extends InvoiceLineItemLike> =
  | {
      document?: { line_items: L[] } | null
      refund_line_item_remaining?: { line_item_id: number; quantity_succeeded: number }[] | null
    }
  | null
  | undefined

export type InvoiceLineItemWithReturnedStatus<L extends InvoiceLineItemLike> = L & {
  remainingQuantity: number
  returnedQuantity: number
}

/** The order's document lines, less tax and discount, with the units refunds credited of each. */
export function getInvoiceLineItemsWithReturnedStatus<L extends InvoiceLineItemLike>(
  order: OrderWithInvoiceLike<L>
): InvoiceLineItemWithReturnedStatus<L>[] {
  const credited = new Map(
    (order?.refund_line_item_remaining ?? []).map((line) => [
      String(line.line_item_id),
      line.quantity_succeeded,
    ])
  )

  return (order?.document?.line_items ?? [])
    .filter(
      (li) =>
        li.type !== TransactionLineType.Tax.Value && li.type !== TransactionLineType.Discount.Value
    )
    .map((invoiceLineItem) => {
      const quantity = Number(invoiceLineItem.quantity ?? 0)
      const returnedQuantity = Math.min(quantity, credited.get(String(invoiceLineItem.id)) ?? 0)

      return {
        ...invoiceLineItem,
        remainingQuantity: quantity - returnedQuantity,
        returnedQuantity,
      }
    })
}

type ThemeColorValue = string | null | undefined

/** Theme colors in the current snake_case schema or the older camelCase one. */
export type ThemeColorInput = {
  primary?: ThemeColorValue
  primary_foreground?: ThemeColorValue
  primaryForeground?: ThemeColorValue
  background?: ThemeColorValue
  foreground?: ThemeColorValue
  secondary?: ThemeColorValue
  secondary_foreground?: ThemeColorValue
  secondaryForeground?: ThemeColorValue
  accent?: ThemeColorValue
  accentSecondary?: ThemeColorValue
  accent_foreground?: ThemeColorValue
  accentSecondaryForeground?: ThemeColorValue
}

export type ThemeColors = {
  primary: ThemeColorValue
  primary_foreground: ThemeColorValue
  background: ThemeColorValue
  foreground: ThemeColorValue
  secondary: ThemeColorValue
  secondary_foreground: ThemeColorValue
  accent: ThemeColorValue
  accent_foreground: ThemeColorValue
}

// It's safe to pass in the new theme naming schema as well.
// Why did we ever name this accentSecondary? Where's the primary accent?
export function convertToNewThemeColors(oldColors: ThemeColorInput = {}): ThemeColors {
  const {
    primary,
    primary_foreground,
    primaryForeground,
    background,
    foreground,
    secondary,
    secondary_foreground,
    secondaryForeground,
    accent,
    accentSecondary,
    accent_foreground,
    accentSecondaryForeground,
  } = oldColors

  return {
    primary,
    primary_foreground: primary_foreground || primaryForeground,
    background,
    foreground,
    secondary,
    secondary_foreground: secondary_foreground || secondaryForeground,
    accent: accent || accentSecondary,
    accent_foreground: accent_foreground || accentSecondaryForeground,
  }
}

type SettingInputComponentName =
  | 'SelectInput'
  | 'SwitchInput'
  | 'TextInput'
  | 'MediaUploadInput'
  | 'SocialUrlInput'
  | 'TextareaInput'

/** The setting fields that decide which input component edits it. */
export type SettingInputDescriptor = {
  options?: unknown
  type?: string | null
}

// `field` is the `form.AppField` render argument; this picks one of its registered components.
export function getSettingInputComponent<
  F extends Partial<Record<SettingInputComponentName, unknown>>,
>(
  field: F | null | undefined,
  setting: SettingInputDescriptor
): F[SettingInputComponentName] | undefined {
  if (setting.options && Array.isArray(setting.options) && setting.options.length > 0) {
    return field?.SelectInput
  }

  switch (setting.type) {
    case 'boolean':
      return field?.SwitchInput
    case 'integer':
      return field?.TextInput
    case 'Media':
      return field?.MediaUploadInput
    case 'Url':
      return field?.SocialUrlInput
    case 'textarea':
      return field?.TextareaInput
    case 'text':
    case 'string':
    case null:
    case undefined:
    default:
      return field?.TextInput
  }
}

// Color validation utilities
export function isColorDark(hexColor: string | null | undefined): boolean {
  const normalizedHex = String(hexColor ?? '').replace(/#/g, '')

  if (normalizedHex.length !== 6) {
    return false
  }

  const red = Number.parseInt(normalizedHex.slice(0, 2), 16)
  const green = Number.parseInt(normalizedHex.slice(2, 4), 16)
  const blue = Number.parseInt(normalizedHex.slice(4, 6), 16)
  const yiq = (red * 2126 + green * 7152 + blue * 722) / 10000

  return yiq < 128
}

export function normalizePhoneDigits(value: string | number | null | undefined): string {
  const digits = String(value ?? '').replace(/\D/g, '')

  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
}

export function isSafeRelativePath(
  pathString: string | null | undefined,
  baseURL: string | URL = window.location.origin
): boolean {
  try {
    if (!pathString) {
      return false
    }
    // Attempt to construct a full URL object from the path and a base
    const resolvedUrl = new URL(pathString, baseURL)

    // Check if the resolved URL is on the same origin
    // This prevents '//malicious.com' and 'https://malicious.com' redirects
    if (resolvedUrl.origin !== new URL(baseURL).origin) {
      return false
    }

    // A safe relative path should resolve to the same origin and should not
    // contain a hostname, protocol, or other absolute URL indicators at the start.
    // This check also ensures the path is not a protocol-relative URL (starting with //)
    const isAbsolute = /^(?:[a-z]+:)?\/\//i.test(pathString)
    return !isAbsolute
  } catch {
    // If URL construction fails, it's likely not a valid path.
    return false
  }
}

/** The location fields the fulfillment date picker reads. */
export type FulfillmentLocationLike =
  | {
      address?: { timezone?: string | null } | null
      earliest_pickup_date?: string | null
      earliest_delivery_date?: string | null
      fulfillment_windows?:
        | { date?: string | null; can_pickup?: boolean | null; can_deliver?: boolean | null }[]
        | null
    }
  | null
  | undefined

export type DisabledDates = {
  min: string
  max: string
  dates: Set<string | null | undefined>
  tz: string
}

// Calculate disabled dates using the same logic as the store
export const calculateDisabledDates = (
  location: FulfillmentLocationLike,
  { isPickup = false }: { isPickup?: boolean }
): DisabledDates => {
  const enabledField = isPickup ? 'can_pickup' : 'can_deliver'
  const tz = location?.address?.timezone || dayjs.tz.guess()
  const earliestDay = isPickup ? location?.earliest_pickup_date : location?.earliest_delivery_date
  const today = dayjs().tz(tz).startOf('day').format('YYYY-MM-DD')

  const windows = location?.fulfillment_windows || []
  let lastDay = windows.length ? windows[windows.length - 1]?.date : today

  if (earliestDay && dayjs(earliestDay).isAfter(dayjs(lastDay), 'day')) {
    lastDay = earliestDay
  }

  // Allows for O(1) look-ups later
  const disabledDates = new Set(windows.filter((w) => !w[enabledField]).map((w) => w?.date))

  return {
    min: earliestDay || today,
    max: lastDay || today,
    dates: disabledDates,
    tz,
  }
}
